import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PermissionKey } from '@/lib/constants/permissions';
import type { EntityType } from '@/lib/constants/entity-types';
import {
  removeMember,
  updateMemberPermission,
  updateMemberRole,
} from '../api/team.api';
import { dataRoomAccessKeys } from '@/hooks/data-room-access.queries';
import { teamKeys } from './team.keys';

/**
 * Member-management mutations for admins (MYN-43): toggle a granular
 * permission, promote/demote a member, and remove a member. Each refreshes the
 * members list on success. Owner-immutability and self-guards are enforced
 * server-side; the UI additionally hides those actions on the owner/self rows.
 */
export function useMemberActions(entityId: string, entityType: EntityType) {
  const queryClient = useQueryClient();
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: teamKeys.members(entityId, entityType) });
  // A removal or a demotion out of Startup Admin releases the person's
  // data-room seat on the server (MYN-91, MYN-245), so the seat section must
  // re-read too, not just the roster.
  const invalidateWithSeats = () =>
    Promise.all([
      invalidate(),
      queryClient.invalidateQueries({ queryKey: dataRoomAccessKeys.forStartup(entityId) }),
    ]);

  const togglePermission = useMutation({
    mutationFn: (vars: {
      membershipId: string;
      permissionKey: PermissionKey;
      isEnabled: boolean;
    }) =>
      updateMemberPermission(
        entityId,
        entityType,
        vars.membershipId,
        vars.permissionKey,
        vars.isEnabled,
      ),
    onSuccess: () => void invalidate(),
  });

  const changeRole = useMutation({
    mutationFn: (vars: { membershipId: string; role: 'admin' | 'member' }) =>
      updateMemberRole(entityId, entityType, vars.membershipId, vars.role),
    onSuccess: () => void invalidateWithSeats(),
  });

  const remove = useMutation({
    mutationFn: (membershipId: string) => removeMember(entityId, entityType, membershipId),
    onSuccess: () => void invalidateWithSeats(),
  });

  return { togglePermission, changeRole, remove };
}
